import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  ZoomIn as ZoomInIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";
import CustomTypography from "../form/CustomTypography";
import { isPdfUrl } from "../../../../utils/urlHelper";

interface ImageData {
  id?: number;
  src: string;
  alt?: string;
}

interface ImageGalleryPopupProps {
  open: boolean;
  images: ImageData[];
  title?: string;
  initialIndex?: number;
  onClose: () => void;
}

const ImageGalleryPopup: React.FC<ImageGalleryPopupProps> = ({
  open,
  images,
  title = "Product Images",
  initialIndex = 0,
  onClose,
}) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setIsZoomed(false);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (images.length === 0) {
      onClose();
    } else if (currentIndex >= images.length) {
      setCurrentIndex(0);
    }
  }, [images, currentIndex, onClose]);

  const handleNext = () => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setIsZoomed(false);
    }
  };

  const handlePrev = () => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      setIsZoomed(false);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - container.left) / container.width) * 100;
    const y = ((e.clientY - container.top) / container.height) * 100;

    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => {
    setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    setIsZoomed(false);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    setIsZoomed(false);
  };

  if (images.length === 0) return null;

  const currentImage = images[currentIndex];
  const currentIsPdf = isPdfUrl(currentImage.src);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: { xs: "90vh", md: "85vh" },
            maxWidth: {
              xs: "85vw",
              sm: "60vw",
              md: "50vw",
              lg: "40vw",
              xl: "35vw",
            },
            width: {
              xs: "85vw",
              sm: "60vw",
              md: "50vw",
              lg: "40vw",
              xl: "35vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CustomTypography variant="heading3">{title}</CustomTypography>
            {images.length > 1 && (
              <Box
                sx={{
                  backgroundColor: theme.palette.customColors.lightPurple,
                  borderRadius: "12px",
                  padding: "4px 12px",
                }}
              >
                <CustomTypography
                  variant="body2"
                  style={{
                    fontSize: "0.8rem",
                    color: theme.palette.customColors.purpleMain,
                    fontWeight: 600,
                  }}
                >
                  {currentIndex + 1} of {images.length}
                </CustomTypography>
              </Box>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: { xs: "auto", md: "visible" },
            maxHeight: { xs: "75vh", md: "none" },
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          {/* Main Image Display */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              ...(currentIsPdf
                ? {
                    aspectRatio: "auto",
                    minHeight: { xs: 280, md: 400 },
                    height: { xs: "62vh", md: "68vh" },
                    maxHeight: { xs: "70vh", md: "75vh" },
                  }
                : {
                    aspectRatio: "1",
                    maxHeight: { xs: "100%", md: "400px", lg: "450px" },
                  }),
              maxWidth: { xs: "100%", md: "400px", lg: "450px" },
              backgroundColor: theme.palette.customColors.lightGreyColor,
              borderRadius: "12px",
              overflow: "hidden",
              mb: { xs: 3, md: 2 },
              mx: "auto",
            }}
          >
            {/* Navigation Arrows - Only show if multiple images */}
            {images.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    backgroundColor: theme.palette.customColors.white,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: theme.palette.customColors.lightPurple,
                      color: theme.palette.customColors.purpleMain,
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>

                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    backgroundColor: theme.palette.customColors.white,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: theme.palette.customColors.lightPurple,
                      color: theme.palette.customColors.purpleMain,
                    },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </>
            )}

            {/* Zoom Indicator (images only) */}
            {!currentIsPdf && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: theme.palette.customColors.white,
                  borderRadius: "8px",
                  padding: "6px 12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
              >
                <ZoomInIcon
                  sx={{
                    fontSize: "1rem",
                    color: theme.palette.customColors.greyTextColor,
                  }}
                />
                <CustomTypography
                  variant="body2"
                  style={{
                    fontSize: "0.75rem",
                    color: theme.palette.customColors.greyTextColor,
                  }}
                >
                  Hover to zoom
                </CustomTypography>
              </Box>
            )}

            {/* Image / PDF */}
            {currentIsPdf ? (
              <Box
                component="iframe"
                src={currentImage.src}
                title={
                  currentImage.alt || `Product document ${currentIndex + 1}`
                }
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                  bgcolor: theme.palette.customColors.white,
                }}
              />
            ) : (
              <Box
                ref={containerRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "zoom-in",
                  overflow: "hidden",
                }}
                onMouseMove={handleMouseMove}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Box
                  component="img"
                  ref={imageRef}
                  src={currentImage.src}
                  alt={currentImage.alt || `Product image ${currentIndex + 1}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    transition: "transform 0.3s ease-in-out",
                    transform: isZoomed ? "scale(2)" : "scale(1)",
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Thumbnails - Only show if multiple images */}
          {images.length > 1 && (
            <Box sx={{ mb: { xs: 2, md: 1 } }}>
              <CustomTypography
                variant="heading6"
                style={{
                  fontWeight: 600,
                  marginBottom: 12,
                  fontSize: "0.9rem",
                }}
              >
                All Images
              </CustomTypography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  pb: 2,
                  "&::-webkit-scrollbar": {
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: theme.palette.customColors.greyBackground,
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.customColors.greyDarkborder,
                    borderRadius: "3px",
                    "&:hover": {
                      backgroundColor: theme.palette.customColors.greyTextColor,
                    },
                  },
                }}
              >
                {images.map((image, index) => (
                  <Box
                    key={image.id ?? index}
                    onClick={() => handleThumbnailClick(index)}
                    sx={{
                      flexShrink: 0,
                      width: 70,
                      height: 70,
                      borderRadius: "8px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: `2px solid ${
                        index === currentIndex
                          ? theme.palette.customColors.purpleMain
                          : theme.palette.customColors.greyBorder
                      }`,
                      backgroundColor: theme.palette.customColors.white,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: theme.palette.customColors.purpleMain,
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 12px ${theme.palette.customColors.purpleMain}30`,
                      },
                    }}
                  >
                    {isPdfUrl(image.src) ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor:
                            theme.palette.customColors.lightGreyColor,
                        }}
                      >
                        <PictureAsPdfIcon
                          sx={{
                            fontSize: 36,
                            color: theme.palette.error.main,
                            opacity: 0.85,
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={image.src}
                        alt={image.alt || `Thumbnail ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default ImageGalleryPopup;
